import React, { Suspense, useEffect, useMemo, useRef } from "react";
import DOMPurify from "dompurify";
import { convertToReactNamingConvention, getCustomizationsRoot, getCurrentTemplate } from "../util/commonFunctions";

export const isCustomSectionId = (id) => typeof id === "string" && id.startsWith("custom::");

const resolveSectionsFromCurrentList = (customizations) => {
  const currentList = customizations?.current?.list;
  const customSectionMap = customizations?.customSections;

  if (!Array.isArray(currentList)) return [];

  return currentList
    .map((sectionId) => {
      if (isCustomSectionId(sectionId)) {
        const custom = customSectionMap?.[sectionId];
        if (!custom) return null;

        return {
          type: "custom",
          id: sectionId,
          html: custom?.html || "",
        };
      }

      return {
        type: "built-in",
        id: sectionId,
      };
    })
    .filter(Boolean);
};

function IsolatedCustomSection({ html }) {
  const hostRef = useRef(null);
  const sanitizedHtml = useMemo(() => DOMPurify.sanitize(html || ""), [html]);

  useEffect(() => {
    if (!hostRef.current) return;

    const shadowRoot = hostRef.current.shadowRoot || hostRef.current.attachShadow({ mode: "open" });

    shadowRoot.innerHTML = "";

    const style = document.createElement("style");
    style.textContent = `
      :host {
        all: initial;
        display: block;
      }

      .custom-section-root {
        all: initial;
        display: block;
        font-family: Arial, sans-serif;
        color: #111827;
        line-height: 1.5;
      }

      .custom-section-root * {
        box-sizing: border-box;
      }

      .custom-section-root img,
      .custom-section-root video,
      .custom-section-root iframe {
        max-width: 100%;
        height: auto;
      }
    `;

    const content = document.createElement("div");
    content.className = "custom-section-root";
    content.innerHTML = sanitizedHtml;

    shadowRoot.appendChild(style);
    shadowRoot.appendChild(content);
  }, [sanitizedHtml]);

  return <div ref={hostRef} />;
}

export default function DynamicSections({ ...props }) {
  let { currentUser } = props;

  const customizations = getCustomizationsRoot(currentUser);
  const template = getCurrentTemplate(currentUser);
  let resolvedSections = resolveSectionsFromCurrentList(customizations);

  // Template 2 renders About Me directly in the page shell, so skip it here to avoid duplication
  if (template === 2) {
    resolvedSections = resolvedSections.filter((s) => s.id !== "About Me");
  }

  const renderSections = () => {
    return resolvedSections.map((section, index) => {
      let component;

      if (section.type === "custom") {
        component = (
          <div className="px-6 py-10 md:px-12 md:py-14">
            <IsolatedCustomSection html={section.html} />
          </div>
        );
      } else {
        let reactId = convertToReactNamingConvention(section.id);
        const Section = React.lazy(() => import(`./dynamic-sections/${reactId}`));
        component = <Section currentUser={currentUser} />;
      }

      // every other background color gray
      let bgColor = index % 2 === 1 ? "bg-gray-100" : "bg-white";

      return (
        <div className={`border-b-2  ${bgColor} `} key={section.id || index}>
          <Suspense fallback={<div></div>}>{component}</Suspense>
        </div>
      );
    });
  };

  return <div>{resolvedSections.length > 0 && renderSections()}</div>;
}
